import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Pressable, Modal, TouchableWithoutFeedback, Alert } from 'react-native';
import { supabase } from '../lib/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/constants';
import { AIAgentStage, StageData, StageName, ClarifyQuestion, PlanOverride } from './AIAgentStage';
import { ProviderStatus, OutreachOwner, ContactAttempt } from './OutreachProviderList';
import { 
  Search, 
  HelpCircle, 
  ClipboardList, 
  Phone, 
  TrendingUp, 
  Calendar,
  Bot,
  Trash2,
  X,
  ChevronDown,
  ChevronUp
} from '../lib/icons';
import { shadows } from '../lib/styles';

interface AIAgentTimelineProps {
  serviceRequestId: string;
  onWorkflowChange?: () => void;
}

interface WorkflowData {
  triage: StageData;
  clarify: StageData;
  plan: StageData;
  outreach: StageData;
  analyze: StageData;
  schedule: StageData;
}

interface WorkflowRecord {
  id: string;
  service_request_id: string;
  current_stage: StageName;
  workflow_data: WorkflowData;
  created_at: string;
  updated_at: string;
}

const mapPlanProvidersToOutreachProviders = (
  providers: any[],
  existingOutreachProviders: any[] = []
) => {
  const existingById = new Map(existingOutreachProviders.map((provider: any) => [provider.id, provider]));
  const isSkippedProvider = (provider: any) =>
    provider?.skipped === true || provider?.skipped === 'true';

  return providers
    .filter((provider: any) => !isSkippedProvider(provider))
    .map((provider: any) => {
      const existing = existingById.get(provider.id);
      return {
        id: provider.id,
        name: provider.name,
        specialty: provider.specialty,
        suggested_contact_method: provider.suggested_contact_method,
        contact_method: existing?.contact_method || provider.contact_method || provider.suggested_contact_method || 'web_form',
        contact_method_reason: provider.contact_method_reason,
        reason: provider.reason,
        outreach_owner: existing?.outreach_owner || provider.outreach_owner || 'human',
        status: existing?.status || 'pending',
        contact_progress: existing?.contact_progress || [],
        last_contacted_at: existing?.last_contacted_at || null,
      };
    });
};

const getPlanProviderRecommendations = (workflowData: WorkflowData): any[] => {
  const planData = (workflowData.plan as any)?.plan_data || {};
  const providerRecommendations = planData.provider_recommendations;
  return Array.isArray(providerRecommendations) ? providerRecommendations : [];
};

const STAGE_DEFINITIONS = [
  {
    name: 'triage' as StageName,
    number: 1,
    title: 'Triage',
    purpose: 'Inspect the request and confirm understanding',
    mockSubsteps: [
      { id: 'triage-1', title: 'Review service request details', completed: false },
      { id: 'triage-2', title: 'Analyze priority and complexity', completed: false },
      { id: 'triage-3', title: 'Identify service category', completed: false },
      { id: 'triage-4', title: 'Generate initial assessment', completed: false },
    ],
    mockNotes: 'Service request appears to be [category]. Priority assessed as [priority]. Estimated complexity: [low/medium/high].',
    confirmationPrompt: 'Proceed to clarification?',
  },
  {
    name: 'clarify' as StageName,
    number: 2,
    title: 'Clarify',
    purpose: 'If we need more information from the homeowner, establish the clarifying questions here',
    mockSubsteps: [
      { id: 'clarify-1', title: 'Analyze request for gaps', completed: false },
      { id: 'clarify-2', title: 'Generate clarifying questions', completed: false },
      { id: 'clarify-3', title: 'Wait for homeowner responses', completed: false },
      { id: 'clarify-4', title: 'Validate information completeness', completed: false },
    ],
    mockNotes: 'No clarification needed - all information is sufficient to proceed.',
    confirmationPrompt: 'Proceed to planning?',
  },
  {
    name: 'plan' as StageName,
    number: 3,
    title: 'Plan',
    purpose: 'When all clarifying questions are answered and we have the info we need, make a plan',
    mockSubsteps: [
      { id: 'plan-1', title: 'Research service options', completed: false },
      { id: 'plan-2', title: 'Identify suitable providers', completed: false },
      { id: 'plan-3', title: 'Draft outreach strategy', completed: false },
      { id: 'plan-4', title: 'Estimate timeline and budget', completed: false },
    ],
    mockNotes: 'Plan: Contact 3-5 providers for quotes. Estimated timeline: 3-5 business days.',
    confirmationPrompt: 'Proceed to outreach?',
  },
  {
    name: 'outreach' as StageName,
    number: 4,
    title: 'Outreach',
    purpose: 'This starts the online research and outreach to service providers. Outreach will be done by AI agents filling out web forms or calling service providers',
    mockSubsteps: [
      { id: 'outreach-1', title: 'Prepare outreach templates', completed: false },
      { id: 'outreach-2', title: 'Submit web forms (mock)', completed: false },
      { id: 'outreach-3', title: 'Make phone calls (mock)', completed: false },
      { id: 'outreach-4', title: 'Track responses', completed: false },
    ],
    mockNotes: 'Contacted 4 providers. Awaiting responses...',
    confirmationPrompt: 'Proceed to analysis?',
  },
  {
    name: 'analyze' as StageName,
    number: 5,
    title: 'Analyze',
    purpose: 'After we have the information back from service providers, we will analyze the information and prepare options for the homeowner to choose from',
    mockSubsteps: [
      { id: 'analyze-1', title: 'Collect provider responses', completed: false },
      { id: 'analyze-2', title: 'Compare pricing and services', completed: false },
      { id: 'analyze-3', title: 'Verify credentials/reviews', completed: false },
      { id: 'analyze-4', title: 'Generate recommendations', completed: false },
    ],
    mockNotes: 'Received 3 quotes. Recommended: ABC Services - Best value at $850.',
    confirmationPrompt: 'Proceed to scheduling?',
  },
  {
    name: 'schedule' as StageName,
    number: 6,
    title: 'Schedule',
    purpose: 'Based on the homeowner\'s choice, we will lock in the chosen service provider and schedule it',
    mockSubsteps: [
      { id: 'schedule-1', title: 'Confirm homeowner selection', completed: false },
      { id: 'schedule-2', title: 'Contact selected provider', completed: false },
      { id: 'schedule-3', title: 'Coordinate scheduling', completed: false },
      { id: 'schedule-4', title: 'Send confirmations', completed: false },
    ],
    mockNotes: 'Service scheduled with ABC Services for Tuesday, Jan 15th at 2:00 PM.',
    confirmationPrompt: null, // Final stage, no confirmation needed
  },
];

export const AIAgentTimeline: React.FC<AIAgentTimelineProps> = ({
  serviceRequestId,
  onWorkflowChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowRecord | null>(null);
  const [showTestingControls, setShowTestingControls] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);

  useEffect(() => {
    fetchWorkflow();
  }, [serviceRequestId]);

  const fetchWorkflow = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('service_request_agent_workflow')
        .select('*')
        .eq('service_request_id', serviceRequestId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No workflow exists, initialize one
          await initializeWorkflow();
        } else {
          throw fetchError;
        }
      } else {
        setWorkflow(data);
      }
    } catch (err: any) {
      console.error('Error fetching workflow:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeWorkflow = async () => {
    try {
      // Create initial workflow data with all stages pending
      const initialWorkflowData: WorkflowData = {
        triage: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        clarify: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        plan: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        outreach: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        analyze: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        schedule: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
      };

      const { data, error: insertError } = await supabase
        .from('service_request_agent_workflow')
        .insert({
          service_request_id: serviceRequestId,
          current_stage: 'triage',
          workflow_data: initialWorkflowData,
        })
        .select()
        .single();

      if (insertError) throw insertError;
      
      setWorkflow(data);
      onWorkflowChange?.();
    } catch (err: any) {
      console.error('Error initializing workflow:', err);
      setError(err.message);
    }
  };

  const updateWorkflow = async (updates: Partial<WorkflowRecord>) => {
    if (!workflow) return;

    setSaving(true);
    try {
      const { data, error: updateError } = await supabase
        .from('service_request_agent_workflow')
        .update(updates)
        .eq('id', workflow.id)
        .select()
        .single();

      if (updateError) throw updateError;
      
      setWorkflow(data);
      onWorkflowChange?.();
    } catch (err: any) {
      console.error('Error updating workflow:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const simulateStageProgress = async (stageName: StageName) => {
    if (!workflow) return;

    // For Stage 1 (Triage), use real OpenAI analysis
    if (stageName === 'triage') {
      await analyzeServiceRequestWithAI();
      return;
    }

    // For Stage 3 (Plan), use real OpenAI planning
    if (stageName === 'plan') {
      await createServicePlanWithAI();
      return;
    }

    // For Stage 5 (Analyze), use real AI quote analysis
    if (stageName === 'analyze') {
      await analyzeProviderResponsesWithAI();
      return;
    }

    // For other stages, use mock simulation
    const stageDefinition = STAGE_DEFINITIONS.find(s => s.name === stageName);
    if (!stageDefinition) return;

    const updatedWorkflowData = { ...workflow.workflow_data };
    const stageData = updatedWorkflowData[stageName];

    // Simulate substep completion
    const substepsWithProgress = stageDefinition.mockSubsteps.map((step, index) => ({
      ...step,
      completed: true, // Mark all as completed for mock
    }));

    updatedWorkflowData[stageName] = {
      ...stageData,
      status: 'awaiting_confirmation',
      substeps: substepsWithProgress,
      notes: stageDefinition.mockNotes,
    };

    await updateWorkflow({
      workflow_data: updatedWorkflowData,
    });
  };

  const analyzeServiceRequestWithAI = async () => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      // Call the edge function to analyze the service request
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/analyze-service-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            service_request_id: serviceRequestId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze service request');
      }

      const result = await response.json();
      console.log('AI Analysis result:', result);

      // Refresh workflow to get updated data
      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error analyzing service request:', err);
      setError(`AI Analysis failed: ${err.message}`);
      
      // Fall back to mock data on error
      const stageDefinition = STAGE_DEFINITIONS.find(s => s.name === 'triage');
      if (stageDefinition) {
        const updatedWorkflowData = { ...workflow.workflow_data };
        updatedWorkflowData.triage = {
          ...updatedWorkflowData.triage,
          status: 'awaiting_confirmation',
          substeps: stageDefinition.mockSubsteps.map(s => ({ ...s, completed: true })),
          notes: `**AI Analysis Error:** ${err.message}\n\n${stageDefinition.mockNotes}`,
          awaiting_confirmation: true,
        };
        
        await updateWorkflow({
          workflow_data: updatedWorkflowData,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async (stageName: StageName) => {
    if (!workflow) return;

    const stageIndex = STAGE_DEFINITIONS.findIndex(s => s.name === stageName);
    const updatedWorkflowData = { ...workflow.workflow_data };
    
    // Complete current stage
    updatedWorkflowData[stageName] = {
      ...updatedWorkflowData[stageName],
      status: 'completed',
      completed_at: new Date().toISOString(),
      awaiting_confirmation: false,
    };

    // Update current stage to next stage if exists, but don't auto-start it
    let newCurrentStage = stageName;
    if (stageIndex < STAGE_DEFINITIONS.length - 1) {
      const nextStage = STAGE_DEFINITIONS[stageIndex + 1];
      newCurrentStage = nextStage.name;
      
      // Don't auto-start the next stage - user must click "Start Agent"
    }

    await updateWorkflow({
      current_stage: newCurrentStage,
      workflow_data: updatedWorkflowData,
    });
  };

  const handleReset = async (stageName: StageName) => {
    if (!workflow) return;

    const stageDefinition = STAGE_DEFINITIONS.find(s => s.name === stageName);
    if (!stageDefinition) return;

    const updatedWorkflowData = { ...workflow.workflow_data };
    
    // Reset the stage to initial state
    // For Clarify stage, preserve clarifying_questions (they come from Triage stage)
    if (stageName === 'clarify') {
      // Keep the clarifying_questions that were set by Stage 1 (Triage)
      // But reset answers so they can be answered again
      const existingQuestions = updatedWorkflowData.clarify.clarifying_questions || [];
      const resetQuestions = existingQuestions.map((q: any) => ({
        ...q,
        answer: '', // Clear answers but keep questions
      }));
      
      updatedWorkflowData[stageName] = {
        status: 'pending',
        started_at: null,
        completed_at: null,
        human_intervention: false,
        notes: '',
        substeps: [],
        awaiting_confirmation: false,
        clarifying_questions: resetQuestions, // Preserve questions, clear answers
      };
    } else {
      // For other stages, reset completely
      if (stageName === 'outreach') {
        const planProviders = getPlanProviderRecommendations(updatedWorkflowData as WorkflowData);
        const mappedProviders = mapPlanProvidersToOutreachProviders(planProviders);
        const providerNotes = mappedProviders.length > 0
          ? `**Provider Outreach Required**\n\nContacting ${mappedProviders.length} service providers for quotes.\n\n**Providers:**\n${mappedProviders.map((provider: any) => `- ${provider.name} (${provider.specialty})`).join('\n')}`
          : '';

        updatedWorkflowData[stageName] = {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: providerNotes,
          substeps: [],
          awaiting_confirmation: false,
          providers_to_contact: mappedProviders,
        } as any;
      } else {
        updatedWorkflowData[stageName] = {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        };
      }
    }

    await updateWorkflow({
      workflow_data: updatedWorkflowData,
    });
    
    // Refresh workflow to update UI
    await fetchWorkflow();
  };

  const handleIntervene = async (stageName: StageName) => {
    if (!workflow) return;

    const updatedWorkflowData = { ...workflow.workflow_data };
    
    updatedWorkflowData[stageName] = {
      ...updatedWorkflowData[stageName],
      human_intervention: true,
      status: 'blocked',
    };

    await updateWorkflow({
      workflow_data: updatedWorkflowData,
    });
  };

  const handleResumeAI = async (stageName: StageName) => {
    if (!workflow) return;

    const updatedWorkflowData = { ...workflow.workflow_data };
    
    updatedWorkflowData[stageName] = {
      ...updatedWorkflowData[stageName],
      human_intervention: false,
      status: 'in_progress',
    };

    await updateWorkflow({
      workflow_data: updatedWorkflowData,
    });

    // Continue simulation for non-outreach stages only.
    setTimeout(() => {
      if (stageName === 'outreach') {
        return;
      }
      simulateStageProgress(stageName);
    }, 1000);
  };

  const handleStartStage = async (stageName: StageName) => {
    if (!workflow) return;

    const stageDefinition = STAGE_DEFINITIONS.find(s => s.name === stageName);
    if (!stageDefinition) return;

    const updatedWorkflowData = { ...workflow.workflow_data };
    
    // For Clarify stage, use existing questions from Stage 1 or extract from notes
    if (stageName === 'clarify') {
      // Check if questions already exist from Stage 1 AI analysis
      // These are stored in clarify.clarifying_questions by the analyze-service-request function
      let questions = updatedWorkflowData.clarify.clarifying_questions || [];
      
      // Filter out any questions that already have answers (reset them)
      questions = questions.map((q: any) => ({
        ...q,
        answer: '', // Ensure all answers are cleared when restarting
      }));
      
      console.log('Starting Clarify stage - existing questions:', questions);
      
      // If no questions exist, try to extract from triage notes (fallback)
      if (questions.length === 0) {
        const triageNotes = updatedWorkflowData.triage.notes || '';
        questions = extractClarifyingQuestions(triageNotes);
        console.log('Extracted questions from triage notes:', questions);
      }
      
      // If still no questions, check if triage stage has stored questions in clarify section
      // (This happens when Stage 1 completes and stores questions for Stage 2)
      if (questions.length === 0 && updatedWorkflowData.clarify.clarifying_questions) {
        questions = updatedWorkflowData.clarify.clarifying_questions.map((q: any) => ({
          ...q,
          answer: '', // Clear answers
        }));
        console.log('Found questions from clarify section:', questions);
      }
      
      updatedWorkflowData[stageName] = {
        ...updatedWorkflowData[stageName],
        status: 'in_progress',
        started_at: new Date().toISOString(),
        substeps: stageDefinition.mockSubsteps.map((s, i) => ({ ...s, completed: i < 2 })),
        clarifying_questions: questions,
      };
      
      console.log('Clarify stage data after start:', updatedWorkflowData[stageName]);
    } else {
      const nextStageData = {
        ...updatedWorkflowData[stageName],
        status: 'in_progress' as const,
        started_at: new Date().toISOString(),
        substeps: stageName === 'outreach' ? [] : stageDefinition.mockSubsteps,
      };

      // For Outreach, hydrate providers from Plan if not already populated.
      if (stageName === 'outreach') {
        const existingOutreachProviders = (updatedWorkflowData.outreach.providers_to_contact || []) as any[];
        const planProviders = getPlanProviderRecommendations(updatedWorkflowData as WorkflowData);

        if (existingOutreachProviders.length === 0 && planProviders.length > 0) {
          const mappedProviders = mapPlanProvidersToOutreachProviders(planProviders);
          const providerCount = mappedProviders.length;
          nextStageData.providers_to_contact = mappedProviders;
          nextStageData.notes = providerCount > 0
            ? `**Provider Outreach Required**\n\nContacting ${providerCount} service providers for quotes.\n\n**Providers:**\n${mappedProviders.map((provider: any) => `- ${provider.name} (${provider.specialty})`).join('\n')}`
            : updatedWorkflowData.outreach.notes || '';
        }
      }

      // Start the stage normally
      updatedWorkflowData[stageName] = nextStageData as any;
    }

    await updateWorkflow({
      current_stage: stageName,
      workflow_data: updatedWorkflowData,
    });

    // Only auto-simulate for non-clarify, non-outreach stages
    if (stageName !== 'clarify' && stageName !== 'outreach') {
      setTimeout(() => {
        simulateStageProgress(stageName);
      }, 2000);
    }
  };

  const handleRunAgentOutreachForProvider = async (providerId: string) => {
    if (!workflow) return;

    const updatedWorkflowData = { ...workflow.workflow_data };
    const outreachProviders = (updatedWorkflowData.outreach.providers_to_contact || []) as any[];
    const now = new Date().toISOString();
    let updatedProviderCount = 0;

    updatedWorkflowData.outreach = {
      ...updatedWorkflowData.outreach,
      providers_to_contact: outreachProviders.map((provider: any) => {
        if (provider.id !== providerId) {
          return provider;
        }

        const outreachOwner = provider.outreach_owner || 'human';
        if (outreachOwner !== 'agent') {
          return provider;
        }

        const contactMethod = provider.contact_method || provider.suggested_contact_method || 'web_form';
        const existingProgress = provider.contact_progress || [];
        updatedProviderCount += 1;

        return {
          ...provider,
          status: provider.status === 'pending' ? 'contacted' : provider.status,
          last_contacted_at: now,
          contact_progress: [
            ...existingProgress,
            {
              id: `agent-attempt-${Date.now()}-${provider.id}`,
              method: contactMethod,
              timestamp: now,
              status: 'success',
              notes: 'AI agent initiated outreach (mock)',
            },
          ],
        };
      }),
      notes: updatedProviderCount > 0
        ? `${updatedWorkflowData.outreach.notes || ''}\n\nAI agent outreach started for ${outreachProviders.find((provider: any) => provider.id === providerId)?.name || 'selected provider'}.`
        : updatedWorkflowData.outreach.notes,
    };

    await updateWorkflow({
      workflow_data: updatedWorkflowData,
    });
  };

  const handleCompleteOutreachManually = async () => {
    if (!workflow) return;

    const updatedWorkflowData = { ...workflow.workflow_data };
    updatedWorkflowData.outreach = {
      ...updatedWorkflowData.outreach,
      status: 'awaiting_confirmation',
      awaiting_confirmation: true,
    };

    await updateWorkflow({
      workflow_data: updatedWorkflowData,
    });
  };

  const extractClarifyingQuestions = (triageNotes: string): ClarifyQuestion[] => {
    // Try to extract questions from the triage notes
    // Looking for patterns like "1. Question?" or "- Question?"
    const lines = triageNotes.split('\n');
    const questions: ClarifyQuestion[] = [];
    
    for (const line of lines) {
      // Match numbered questions or bullet points
      const match = line.match(/^[\s]*[\d\-\*]+[\.\)]\s*(.+\?)\s*$/);
      if (match) {
        questions.push({
          id: `clarify-q-${questions.length + 1}`,
          question: match[1].trim(),
          answer: '',
        });
      }
    }
    
    // If no questions found, check if notes say "no questions needed"
    if (questions.length === 0 && triageNotes.toLowerCase().includes('no clarification needed')) {
      return [];
    }
    
    return questions;
  };

  const createServicePlanWithAI = async () => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      // Call the edge function to create a service plan
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/create-service-plan`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            service_request_id: serviceRequestId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create service plan');
      }

      const result = await response.json();
      console.log('Service plan result:', result);

      // Refresh workflow to get updated data
      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error creating service plan:', err);
      setError(`Service plan creation failed: ${err.message}`);
      
      // Fall back to mock data on error
      const stageDefinition = STAGE_DEFINITIONS.find(s => s.name === 'plan');
      if (stageDefinition) {
        const updatedWorkflowData = { ...workflow.workflow_data };
        updatedWorkflowData.plan = {
          ...updatedWorkflowData.plan,
          status: 'awaiting_confirmation',
          substeps: stageDefinition.mockSubsteps.map(s => ({ ...s, completed: true })),
          notes: `**AI Planning Error:** ${err.message}\n\n${stageDefinition.mockNotes}`,
          awaiting_confirmation: true,
        };
        
        await updateWorkflow({
          workflow_data: updatedWorkflowData,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const analyzeProviderResponsesWithAI = async () => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('You are not authenticated. Please refresh and sign in again.');
      }

      let { data: result, error: invokeError } = await supabase.functions.invoke(
        'provider-options',
        {
          body: { service_request_id: serviceRequestId },
        }
      );

      if (invokeError) {
        console.error('Analyze invoke error details:', {
          name: (invokeError as any)?.name,
          message: (invokeError as any)?.message,
          context: (invokeError as any)?.context,
        });

        // Fallback to raw fetch when supabase-js transport fails in browser.
        if ((invokeError as any)?.name === 'FunctionsFetchError') {
          try {
            const fallbackResponse = await fetch(`${SUPABASE_URL}/functions/v1/provider-options`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                apikey: SUPABASE_ANON_KEY,
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ service_request_id: serviceRequestId }),
            });

            if (!fallbackResponse.ok) {
              const fallbackError = await fallbackResponse.text();
              throw new Error(`Fallback fetch failed (${fallbackResponse.status}): ${fallbackError}`);
            }

            result = await fallbackResponse.json();
            invokeError = null as any;
            console.log('Analyze fallback fetch succeeded');
          } catch (fallbackErr: any) {
            console.error('Analyze fallback fetch failed:', fallbackErr);
            const message = invokeError.message || 'Failed to analyze provider responses';
            throw new Error(
              `${message}. Raw fetch fallback also failed: ${fallbackErr?.message || 'Unknown error'}`
            );
          }
        } else {
          const message = invokeError.message || 'Failed to analyze provider responses';
          throw new Error(
            `${message}. If this is a network error, confirm the function is deployed and CORS is enabled.`
          );
        }
      }

      console.log('Analyze stage result:', result);

      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error analyzing provider responses:', err);
      setError(`Analyze stage failed: ${err.message}`);

      const stageDefinition = STAGE_DEFINITIONS.find(s => s.name === 'analyze');
      if (stageDefinition) {
        const updatedWorkflowData = { ...workflow.workflow_data };
        updatedWorkflowData.analyze = {
          ...updatedWorkflowData.analyze,
          status: 'awaiting_confirmation',
          substeps: stageDefinition.mockSubsteps.map(s => ({ ...s, completed: true })),
          notes: `**AI Analyze Error:** ${err.message}\n\n${stageDefinition.mockNotes}`,
          awaiting_confirmation: true,
        } as any;

        await updateWorkflow({
          workflow_data: updatedWorkflowData,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitClarifyAnswers = async (answers: ClarifyQuestion[]) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/process-clarifying-answers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            service_request_id: serviceRequestId,
            questions_and_answers: answers.map(qa => ({
              question: qa.question,
              answer: qa.answer,
            })),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process answers');
      }

      const result = await response.json();
      console.log('Clarify answers result:', result);
      console.log('Sufficient information:', result.sufficient_information);
      console.log('Edge function returned status:', result.clarify_status);
      console.log('Edge function returned awaiting_confirmation:', result.awaiting_confirmation);

      // Refresh workflow to get updated data from edge function
      // The edge function handles all workflow updates including:
      // - Setting awaiting_confirmation if sufficient_information is true
      // - Adding new questions if more info is needed
      // - Updating substeps
      await fetchWorkflow();
      
      // Wait a moment for database to sync, then check again
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Re-fetch workflow to get latest state
      const { data: latestWorkflow, error: fetchError } = await supabase
        .from('service_request_agent_workflow')
        .select('*')
        .eq('service_request_id', serviceRequestId)
        .single();
        
      if (fetchError) {
        console.error('Error fetching latest workflow:', fetchError);
      } else if (latestWorkflow) {
        const clarifyStatus = latestWorkflow.workflow_data.clarify?.status;
        const awaitingConfirmation = latestWorkflow.workflow_data.clarify?.awaiting_confirmation;
        
        console.log('After refresh - clarify status:', clarifyStatus);
        console.log('After refresh - awaiting_confirmation:', awaitingConfirmation);
        
        // Update local state with latest workflow
        setWorkflow(latestWorkflow);
        
        // Double-check: If the edge function said sufficient_information but workflow didn't update,
        // manually set awaiting_confirmation (fallback)
        if (result.sufficient_information && (clarifyStatus !== 'awaiting_confirmation' || !awaitingConfirmation)) {
          console.warn('Edge function said sufficient_information but status not updated, fixing...');
          const updatedWorkflowData = { ...latestWorkflow.workflow_data };
          updatedWorkflowData.clarify = {
            ...updatedWorkflowData.clarify,
            status: 'awaiting_confirmation',
            awaiting_confirmation: true,
            clarifying_questions: [], // Clear questions
            substeps: [
              { id: 'clarify-1', title: 'Analyze request for gaps', completed: true },
              { id: 'clarify-2', title: 'Generate clarifying questions', completed: true },
              { id: 'clarify-3', title: 'Receive homeowner responses', completed: true },
              { id: 'clarify-4', title: 'Validate information completeness', completed: true },
            ],
          };
          await updateWorkflow({
            workflow_data: updatedWorkflowData,
          });
          await fetchWorkflow();
        }
      }

      // If more questions were added, workflow is already updated
      // If sufficient info, workflow is set to awaiting_confirmation
    } catch (err: any) {
      console.error('Error processing clarify answers:', err);
      setError(`Failed to process answers: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleProvidersChange = async (providers: any[]) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const updatedWorkflowData = { ...workflow.workflow_data };
      
      // Update plan data with modified providers
      if (updatedWorkflowData.plan.plan_data) {
        updatedWorkflowData.plan.plan_data = {
          ...updatedWorkflowData.plan.plan_data,
          provider_recommendations: providers,
        };
      }

      // Update outreach stage with active (non-skipped) providers
      const activeProviders = providers.filter((provider: any) => provider?.skipped !== true && provider?.skipped !== 'true');
      const mappedOutreachProviders = mapPlanProvidersToOutreachProviders(
        providers,
        (updatedWorkflowData.outreach.providers_to_contact || []) as any[]
      );
      updatedWorkflowData.outreach = {
        ...updatedWorkflowData.outreach,
        providers_to_contact: mappedOutreachProviders,
        notes: `**Provider Outreach Required**\n\nContacting ${activeProviders.length} service providers for quotes.\n\n**Providers:**\n${activeProviders.map((p: any) => `- ${p.name} (${p.specialty})`).join('\n')}`,
      };

      await updateWorkflow({
        workflow_data: updatedWorkflowData,
      });
      
      // Refresh workflow to update UI
      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error updating providers:', err);
      setError(`Failed to update providers: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleContactMethodChange = async (providerId: string, method: string) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const updatedWorkflowData = { ...workflow.workflow_data };
      
      // Find and update the provider's contact_method in outreach stage
      if (updatedWorkflowData.outreach?.providers_to_contact) {
        updatedWorkflowData.outreach.providers_to_contact = updatedWorkflowData.outreach.providers_to_contact.map((p: any) => {
          if (p.id === providerId) {
            return {
              ...p,
              contact_method: method,
              // Keep suggested_contact_method unchanged to show the override
            };
          }
          return p;
        });
      }

      await updateWorkflow({
        workflow_data: updatedWorkflowData,
      });
      
      // Refresh workflow to update UI
      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error updating contact method:', err);
      setError(`Failed to update contact method: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePlanOverride = async (override: PlanOverride, reason: string) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/override-service-plan`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            service_request_id: serviceRequestId,
            override_type: override,
            override_reason: reason,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to apply override');
      }

      const result = await response.json();
      console.log('Plan override result:', result);

      // Refresh workflow to get updated data
      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error applying plan override:', err);
      setError(`Override failed: ${err.message}`);
      throw err; // Re-throw so form can handle it
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyzeOverride = async (options: any[], recommendedOptionId: string, reason: string) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('You are not authenticated. Please refresh and sign in again.');
      }

      let { data: result, error: invokeError } = await supabase.functions.invoke(
        'provider-options-override',
        {
          body: {
            service_request_id: serviceRequestId,
            override_reason: reason,
            homeowner_options: options,
            recommended_option_id: recommendedOptionId,
          },
        }
      );

      if (invokeError) {
        if ((invokeError as any)?.name === 'FunctionsFetchError') {
          try {
            const fallbackResponse = await fetch(`${SUPABASE_URL}/functions/v1/provider-options-override`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                apikey: SUPABASE_ANON_KEY,
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                service_request_id: serviceRequestId,
                override_reason: reason,
                homeowner_options: options,
                recommended_option_id: recommendedOptionId,
              }),
            });

            if (!fallbackResponse.ok) {
              const fallbackError = await fallbackResponse.text();
              throw new Error(`Fallback fetch failed (${fallbackResponse.status}): ${fallbackError}`);
            }

            result = await fallbackResponse.json();
            invokeError = null as any;
            console.log('Analyze override fallback fetch succeeded');
          } catch (fallbackErr: any) {
            throw new Error(
              `${invokeError.message || 'Failed to apply analyze override'}. Raw fetch fallback failed: ${fallbackErr?.message || 'Unknown error'}`
            );
          }
        } else {
          throw new Error(invokeError.message || 'Failed to apply analyze override');
        }
      }

      console.log('Analyze override result:', result);
      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error applying analyze override:', err);
      setError(`Analyze override failed: ${err.message}`);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleProviderStatusChange = async (providerId: string, status: ProviderStatus) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const updatedWorkflowData = { ...workflow.workflow_data };
      
      // Update provider status in outreach stage
      if (updatedWorkflowData.outreach.providers_to_contact) {
        updatedWorkflowData.outreach.providers_to_contact = 
          updatedWorkflowData.outreach.providers_to_contact.map((p: any) => {
            if (p.id === providerId) {
              return {
                ...p,
                status,
                last_contacted_at: status !== 'pending' ? new Date().toISOString() : p.last_contacted_at,
              };
            }
            return p;
          });
      }

      await updateWorkflow({
        workflow_data: updatedWorkflowData,
      });
      
      // Refresh workflow to update UI
      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error updating provider status:', err);
      setError(`Failed to update provider status: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleContactProgressChange = async (providerId: string, contactProgress: ContactAttempt[]) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const updatedWorkflowData = { ...workflow.workflow_data };
      const latestAttempt = contactProgress.length > 0 ? contactProgress[contactProgress.length - 1] : null;

      if (updatedWorkflowData.outreach.providers_to_contact) {
        updatedWorkflowData.outreach.providers_to_contact =
          updatedWorkflowData.outreach.providers_to_contact.map((provider: any) => {
            if (provider.id === providerId) {
              return {
                ...provider,
                contact_progress: contactProgress,
                last_contacted_at: latestAttempt?.timestamp || provider.last_contacted_at,
                status: provider.status === 'pending' && latestAttempt ? 'contacted' : provider.status,
              };
            }
            return provider;
          });
      }

      await updateWorkflow({
        workflow_data: updatedWorkflowData,
      });

      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error updating contact progress:', err);
      setError(`Failed to update contact progress: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleOutreachOwnerChange = async (providerId: string, owner: OutreachOwner) => {
    if (!workflow) return;

    setSaving(true);
    setError(null);

    try {
      const updatedWorkflowData = { ...workflow.workflow_data };

      if (updatedWorkflowData.outreach.providers_to_contact) {
        updatedWorkflowData.outreach.providers_to_contact =
          updatedWorkflowData.outreach.providers_to_contact.map((provider: any) => {
            if (provider.id === providerId) {
              return {
                ...provider,
                outreach_owner: owner,
              };
            }
            return provider;
          });
      }

      await updateWorkflow({
        workflow_data: updatedWorkflowData,
      });

      await fetchWorkflow();
    } catch (err: any) {
      console.error('Error updating outreach owner:', err);
      setError(`Failed to update outreach owner: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const canStartStage = (stageName: StageName): boolean => {
    if (!workflow) return false;

    const stageIndex = STAGE_DEFINITIONS.findIndex(s => s.name === stageName);
    
    // First stage can always be started if pending
    if (stageIndex === 0) {
      return workflow.workflow_data[stageName].status === 'pending';
    }

    // Other stages can only be started if previous stage is completed
    const previousStage = STAGE_DEFINITIONS[stageIndex - 1];
    const previousStageData = workflow.workflow_data[previousStage.name];
    const currentStageData = workflow.workflow_data[stageName];

    return previousStageData.status === 'completed' && currentStageData.status === 'pending';
  };

  const handleClearAll = async () => {
    setShowClearAllConfirm(false);
    
    try {
      // Delete the workflow record
      const { error: deleteError } = await supabase
        .from('service_request_agent_workflow')
        .delete()
        .eq('id', workflow?.id);

      if (deleteError) throw deleteError;

      // Reinitialize
      await initializeWorkflow();
      onWorkflowChange?.();
    } catch (err: any) {
      console.error('Error clearing workflow:', err);
      setError(err.message);
    }
  };

  const getStageIcon = (stageName: StageName) => {
    const iconColor = '#6B7280';
    const iconSize = 20;
    
    switch (stageName) {
      case 'triage':
        return <Search size={iconSize} color={iconColor} />;
      case 'clarify':
        return <HelpCircle size={iconSize} color={iconColor} />;
      case 'plan':
        return <ClipboardList size={iconSize} color={iconColor} />;
      case 'outreach':
        return <Phone size={iconSize} color={iconColor} />;
      case 'analyze':
        return <TrendingUp size={iconSize} color={iconColor} />;
      case 'schedule':
        return <Calendar size={iconSize} color={iconColor} />;
      default:
        return <Bot size={iconSize} color={iconColor} />;
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#2563EB" />
        <Text style={styles.loadingText}>Loading AI Agent...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{error}</Text>
        <Pressable style={styles.retryButton} onPress={fetchWorkflow}>
          <Text style={styles.retryButtonText}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  if (!workflow) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Unable to load workflow</Text>
      </View>
    );
  }

  const effectiveOutreachProviders = (() => {
    const existingOutreachProviders = (workflow.workflow_data.outreach?.providers_to_contact || []) as any[];
    if (existingOutreachProviders.length > 0) {
      return existingOutreachProviders;
    }

    const planProviders = getPlanProviderRecommendations(workflow.workflow_data);
    if (planProviders.length === 0) {
      return [];
    }

    return mapPlanProvidersToOutreachProviders(planProviders);
  })();

  const effectiveOutreachNotes = effectiveOutreachProviders.length > 0
    ? `**Provider Outreach Required**\n\nContacting ${effectiveOutreachProviders.length} service providers for quotes.\n\n**Providers:**\n${effectiveOutreachProviders.map((provider: any) => `- ${provider.name} (${provider.specialty})`).join('\n')}`
    : workflow.workflow_data.outreach?.notes || '';

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerIcon}>
          <Bot size={24} color="#2563EB" />
        </View>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>AI Agent Assistant</Text>
          <Text style={styles.headerSubtitle}>Guided workflow automation</Text>
        </View>
      </View>

      {/* Timeline */}
      <View style={styles.timeline}>
        {STAGE_DEFINITIONS.map((stage) => {
          const stageData = stage.name === 'outreach'
            ? ({
                ...workflow.workflow_data[stage.name],
                providers_to_contact: effectiveOutreachProviders,
                notes: workflow.workflow_data[stage.name].notes || effectiveOutreachNotes,
              } as any)
            : workflow.workflow_data[stage.name];

          return (
          <AIAgentStage
            key={stage.name}
            stageName={stage.name}
            stageData={stageData}
            stageNumber={stage.number}
            stageTitle={stage.title}
            stagePurpose={stage.purpose}
            isActive={workflow.current_stage === stage.name}
            icon={getStageIcon(stage.name)}
            onConfirm={() => handleConfirm(stage.name)}
            onReset={() => handleReset(stage.name)}
            onIntervene={() => handleIntervene(stage.name)}
            onResumeAI={() => handleResumeAI(stage.name)}
            onStart={() => handleStartStage(stage.name)}
            onSubmitClarifyAnswers={stage.name === 'clarify' ? handleSubmitClarifyAnswers : undefined}
            onPlanOverride={stage.name === 'plan' ? handlePlanOverride : undefined}
            onAnalyzeOverride={stage.name === 'analyze' ? handleAnalyzeOverride : undefined}
            onProvidersChange={stage.name === 'plan' ? handleProvidersChange : undefined}
            onProviderStatusChange={stage.name === 'outreach' ? handleProviderStatusChange : undefined}
            onContactProgressChange={stage.name === 'outreach' ? handleContactProgressChange : undefined}
            onContactMethodChange={stage.name === 'outreach' ? handleContactMethodChange : undefined}
            onOutreachOwnerChange={stage.name === 'outreach' ? handleOutreachOwnerChange : undefined}
            onRunProviderOutreach={stage.name === 'outreach' ? handleRunAgentOutreachForProvider : undefined}
            onCompleteManually={stage.name === 'outreach' ? handleCompleteOutreachManually : undefined}
            canStart={canStartStage(stage.name)}
            disabled={saving}
          />
          );
        })}
      </View>

      {/* Testing Controls */}
      <View style={styles.testingSection}>
        <Pressable
          style={styles.testingHeader}
          onPress={() => setShowTestingControls(!showTestingControls)}
        >
          <Text style={styles.testingTitle}>Testing Controls</Text>
          {showTestingControls ? (
            <ChevronUp size={20} color="#6B7280" />
          ) : (
            <ChevronDown size={20} color="#6B7280" />
          )}
        </Pressable>
        
        {showTestingControls && (
          <View style={styles.testingControls}>
            <Pressable
              style={styles.testingButton}
              onPress={() => setShowClearAllConfirm(true)}
              disabled={saving}
            >
              <Trash2 size={16} color="#DC2626" />
              <Text style={styles.testingButtonText}>Clear All Progress</Text>
            </Pressable>
          </View>
        )}
      </View>

      {/* Clear All Confirmation Modal */}
      <Modal
        visible={showClearAllConfirm}
        transparent
        animationType="fade"
        onRequestClose={() => setShowClearAllConfirm(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowClearAllConfirm(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Clear All Progress</Text>
                  <Pressable
                    onPress={() => setShowClearAllConfirm(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9CA3AF" />
                  </Pressable>
                </View>
                <View style={styles.modalBody}>
                  <Text style={styles.modalText}>
                    Are you sure you want to clear all workflow progress? This will reset all stages back to the beginning and cannot be undone.
                  </Text>
                </View>
                <View style={styles.modalActions}>
                  <Pressable
                    style={[styles.modalButton, styles.modalCancelButton]}
                    onPress={() => setShowClearAllConfirm(false)}
                  >
                    <Text style={styles.modalCancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.modalButton, styles.modalDeleteButton]}
                    onPress={handleClearAll}
                  >
                    <Text style={styles.modalDeleteButtonText}>Clear All</Text>
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  loadingContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 48,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    ...shadows.sm,
  },
  loadingText: {
    fontSize: 14,
    color: '#6B7280',
  },
  errorContainer: {
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    padding: 24,
    gap: 16,
    ...shadows.sm,
  },
  errorText: {
    fontSize: 14,
    color: '#DC2626',
    textAlign: 'center',
  },
  retryButton: {
    backgroundColor: '#DC2626',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    alignSelf: 'center',
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  header: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    ...shadows.sm,
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#EFF6FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#6B7280',
  },
  timeline: {
    gap: 0,
  },
  testingSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    ...shadows.sm,
  },
  testingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  testingTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  testingControls: {
    padding: 16,
    gap: 12,
  },
  testingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCA5A5',
    backgroundColor: '#FFFFFF',
  },
  testingButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#DC2626',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    ...shadows.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalBody: {
    padding: 24,
  },
  modalText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  modalButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    minWidth: 100,
    alignItems: 'center',
  },
  modalCancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  modalCancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  modalDeleteButton: {
    backgroundColor: '#DC2626',
  },
  modalDeleteButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
